// Zentrale Logik für die Mangel-Regel (defect_trigger) eines Wartungsfeldes.
// Spiegelt die Backend-Regeln aus api/utils/maintenance_schema.py:
//   1. expliziter options_override_json.defect_trigger (mode equals|in, values nicht leer)
//   2. sonst: aus Optionen mit is_defect=true abgeleitet (mode "in")
// Zusätzlich liefert dieses Modul den UI-Standard für Ja/Nein-Felder
// ("Ja" = Mangel), damit das, was der Editor anzeigt, auch gespeichert wird.

import { normalizeFieldType } from "@/lib/fieldTypeCatalog";
import type { BuilderField, FieldOption } from "./fieldModel";

export const DEFECT_TRIGGER_OPTION_TYPES = new Set([
  "select",
  "multiselect",
  "choice",
  "article_ref",
  "article_ref_list",
]);

export type DefectTriggerMode = "equals" | "in";

export interface DefectTrigger {
  mode: DefectTriggerMode;
  values: (string | number | boolean)[];
  grade_effect?: number | null;
}

export type DefectTriggerSource = "explicit" | "options" | "server" | "default";

export interface ResolvedDefectTrigger {
  trigger: DefectTrigger;
  source: DefectTriggerSource;
}

export function normalizeDefectFieldType(rawType: unknown): string {
  const raw = String(rawType ?? "").trim().toLowerCase();
  return normalizeFieldType(raw) || raw;
}

export function isBooleanDefectType(rawType: unknown): boolean {
  const type = normalizeDefectFieldType(rawType);
  return type === "checkbox" || type === "bool" || type === "boolean";
}

export function isOptionDefectType(rawType: unknown): boolean {
  return DEFECT_TRIGGER_OPTION_TYPES.has(normalizeDefectFieldType(rawType));
}

export function fieldSupportsDefectTrigger(rawType: unknown): boolean {
  return isBooleanDefectType(rawType) || isOptionDefectType(rawType);
}

export function isDefectRoleField(field: Pick<BuilderField, "scope" | "semantic_role">): boolean {
  return field.scope === "inspection" && String(field.semantic_role || "").trim().toLowerCase() === "defect";
}

export function normalizeDefectComparableValue(value: unknown): string {
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value ?? "").trim().toLowerCase();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseGradeEffect(raw: unknown): number | null | undefined {
  if (raw == null || String(raw).trim() === "") return undefined;
  const parsed = Number.parseInt(String(raw).trim(), 10);
  return Number.isInteger(parsed) ? parsed : null;
}

/** Validiert einen expliziten defect_trigger. Gibt null zurück, wenn ungültig/fehlend. */
export function parseDefectTrigger(raw: unknown): DefectTrigger | null {
  const obj = asRecord(raw);
  if (!obj) return null;
  const mode = String(obj.mode || "").trim().toLowerCase();
  if (mode !== "equals" && mode !== "in") return null;
  const rawValues = Array.isArray(obj.values) ? obj.values : [];
  const values = rawValues.filter(
    (value): value is string | number | boolean =>
      typeof value === "string" || typeof value === "number" || typeof value === "boolean",
  );
  if (!values.some((value) => normalizeDefectComparableValue(value) !== "")) return null;
  const trigger: DefectTrigger = { mode, values };
  const grade = parseGradeEffect(obj.grade_effect);
  if (grade !== undefined) trigger.grade_effect = grade;
  return trigger;
}

/** Ist ein expliziter defect_trigger vorhanden, aber unbrauchbar (z. B. nur grade_effect)? */
export function hasBrokenExplicitDefectTrigger(optionsConfig: Record<string, unknown>): boolean {
  const raw = optionsConfig.defect_trigger;
  return raw != null && asRecord(raw) != null && parseDefectTrigger(raw) === null;
}

export function getOptionRows(optionsConfig: Record<string, unknown> | null | undefined): FieldOption[] {
  if (!optionsConfig) return [];
  for (const key of ["options", "choices", "items"]) {
    const raw = optionsConfig[key];
    if (Array.isArray(raw)) return raw.filter((row): row is FieldOption => asRecord(row) != null);
  }
  return [];
}

/** Trigger aus Optionen mit is_defect=true ableiten (Legacy-/Options-Modus). */
export function deriveDefectTriggerFromOptions(
  optionsConfig: Record<string, unknown> | null | undefined,
): DefectTrigger | null {
  const rows = getOptionRows(optionsConfig);
  if (!rows.length) return null;
  const values: string[] = [];
  for (const row of rows) {
    if (row.is_defect !== true) continue;
    const normalized = normalizeDefectComparableValue(row.value);
    if (!normalized || values.includes(normalized)) continue;
    values.push(normalized);
  }
  return values.length ? { mode: "in", values } : null;
}

/** UI-Standard für Ja/Nein-Felder: angehakt = Mangel. */
export function defaultDefectTriggerForType(rawType: unknown): DefectTrigger | null {
  return isBooleanDefectType(rawType) ? { mode: "equals", values: [true] } : null;
}

/**
 * Effektiver Trigger wie ihn das Backend beim Publizieren prüft:
 * explizit → aus Optionen abgeleitet. Kein UI-Default (siehe materialize…).
 */
export function resolveDefectTrigger(
  field: Pick<BuilderField, "type" | "options_override_json">,
): ResolvedDefectTrigger | null {
  const options = asRecord(field.options_override_json) || {};
  const explicit = parseDefectTrigger(options.defect_trigger);
  if (explicit) return { trigger: explicit, source: "explicit" };
  // Ein kaputter expliziter Trigger blockiert auch im Backend den Options-Fallback.
  if (hasBrokenExplicitDefectTrigger(options)) return null;
  if (isOptionDefectType(field.type)) {
    const derived = deriveDefectTriggerFromOptions(options);
    if (derived) return { trigger: derived, source: "options" };
  }
  return null;
}

/**
 * Sorgt dafür, dass ein Mangel-Feld einen gespeicherten Trigger hat, der dem
 * entspricht, was der Editor anzeigt. Reihenfolge: bereits gültig → vom Server
 * aufgelöster Trigger (z. B. Template-Default) → UI-Standard für Ja/Nein.
 * Gibt die Quelle zurück, wenn etwas ergänzt wurde, sonst null.
 */
export function materializeDefectTrigger(
  field: BuilderField,
  serverTrigger?: unknown,
): DefectTriggerSource | null {
  if (!isDefectRoleField(field)) return null;
  const options = asRecord(field.options_override_json) || {};
  if (parseDefectTrigger(options.defect_trigger)) return null;
  const broken = hasBrokenExplicitDefectTrigger(options);

  if (!broken && isOptionDefectType(field.type) && deriveDefectTriggerFromOptions(options)) {
    return null;
  }

  const fromServer = parseDefectTrigger(serverTrigger);
  if (fromServer) {
    field.options_override_json = { ...options, defect_trigger: fromServer };
    return "server";
  }

  const fallback = defaultDefectTriggerForType(field.type);
  if (fallback) {
    const previous = asRecord(options.defect_trigger) || {};
    const grade = parseGradeEffect(previous.grade_effect);
    const next: DefectTrigger = { ...fallback };
    if (grade != null) next.grade_effect = grade;
    field.options_override_json = { ...options, defect_trigger: next };
    return "default";
  }

  if (broken) {
    // Unbrauchbaren Trigger entfernen, damit der Options-Fallback greifen kann.
    const { defect_trigger: _removed, ...rest } = options;
    field.options_override_json = rest;
  }
  return null;
}

/** Entfernt defect_trigger, wenn die Rolle nicht (mehr) „Mangel“ ist. */
export function stripDefectTriggerIfNotDefect(field: BuilderField): void {
  if (isDefectRoleField(field)) return;
  const options = asRecord(field.options_override_json);
  if (!options || !("defect_trigger" in options)) return;
  const { defect_trigger: _removed, ...rest } = options;
  field.options_override_json = rest;
}

export function getDefectTriggerGradeEffect(trigger: DefectTrigger | null | undefined): number | null {
  if (!trigger) return null;
  const grade = parseGradeEffect(trigger.grade_effect);
  return grade ?? null;
}

/** Anzahl der als Mangel markierten Optionen. */
export function countDefectOptions(optionsConfig: Record<string, unknown> | null | undefined): {
  defect: number;
  total: number;
} {
  const rows = getOptionRows(optionsConfig);
  return {
    defect: rows.filter((row) => row.is_defect === true && normalizeDefectComparableValue(row.value)).length,
    total: rows.length,
  };
}
